import { create } from "zustand";
import {
  attachment_cell_names,
  DisplayGroup,
  Filter,
  FilterGroupBuilder,
  FilterOperator,
  LogicalOperator,
  PhysicalKind,
  get_operators_for_type,
} from "target_wasm";
import { createLogger } from "debug/DebugingLogger";
import {
  conditionsAbout,
  unnamedParts,
} from "components/common/FilterBuilder/conditions";
import type {
  DropTarget,
  FilterEditorStore,
} from "components/common/FilterBuilder/FilterGroupTypes";

const logger = createLogger("FilterStore");

const HIGHLIGHT_MS = 2000;
/** How long a peek outlives the pointer leaving, to cross from Filter into it. */
const PEEK_GRACE_MS = 150;

/** The column named by Filter by this column / value. */
export interface FilterColumn {
  id: string;
  name: string;
  dataType: PhysicalKind;
}

/** How a cell's value filters its column: Is Null for an empty cell, else the
 *  column's equality — a categorical has only Contains; none for a kind that
 *  offers neither. */
export const cellOperator = (
  dataType: PhysicalKind,
  value: string | null,
): FilterOperator | null => {
  const offered = get_operators_for_type(dataType);
  if (value === null)
    return offered.includes(FilterOperator.IsNull)
      ? FilterOperator.IsNull
      : null;
  // A file cell filters on its files' names; a cell none can be read from has none.
  if (
    dataType === PhysicalKind.Attachment &&
    attachment_cell_names(value).length === 0
  )
    return null;
  return (
    [FilterOperator.Equal, FilterOperator.Contains].find((op) =>
      offered.includes(op),
    ) ?? null
  );
};

/** A hovered Filter on the bar: its view's filter shows read-only above it. */
export interface FilterPeek {
  viewId: string;
  anchor: HTMLElement;
  /** A column's or a cell's Filter: that column's conditions are lit. */
  columnId?: string;
  /** A cell's Filter: its value, null for an empty cell. */
  cellValue?: string | null;
  /** Pressed, not hovered: the pointer leaving does not take it away. */
  pinned?: boolean;
  /** Its Edit: the editor its Filter opened before the filter was in force. */
  edit?: () => void;
}

interface FilterStoreState {
  /** The view the editor is open on, or none. */
  filterDialogOpenViewId: string | null;
  /** The bar slot it opened from; none from the pane. */
  anchor: HTMLElement | null;
  /** The view had a filter when the editor opened: Cancel goes back to reading it. */
  hasApplied: boolean;
  builder: FilterGroupBuilder | null;
  structure: DisplayGroup | null;
  hoveredGroupId: string | null;
  draggedConditionId: string | null;
  dropTarget: DropTarget | null;
  /** Conditions and groups lit as just added or moved. */
  highlighted: ReadonlySet<string>;
  /** Conditions the read view lit, easing out as the editor opens on them. */
  fading: ReadonlySet<string>;
  peek: FilterPeek | null;
}

interface FilterStoreActions {
  openFilterDialog: (
    viewId: string,
    filter?: Filter | null,
    anchor?: HTMLElement | null,
  ) => void;
  /** Opens the view's filter for a column, or a cell's value in it (null for
   *  an empty cell): on the conditions it already has for that, else on a new
   *  condition set to it (`cellOperator`). Answers the condition's id. */
  openFilterFor: (
    viewId: string,
    filter: Filter | null | undefined,
    column: FilterColumn,
    from?: { anchor?: HTMLElement | null; cellValue?: string | null },
  ) => string | null;
  closeDialogAndReset: () => void;
  /** Drops the edits and reads the filter as applied, where it was edited;
   *  from the pane, with no slot to read it over, or with none applied, closes. */
  cancelEdit: () => void;
  /** Shows a peek, or closes it at once. */
  setPeek: (peek: FilterPeek | null) => void;
  /** Closes the peek after `PEEK_GRACE_MS`, unless held first. */
  releasePeek: () => void;
  holdPeek: () => void;
}

export type FilterStore = FilterStoreState &
  FilterStoreActions &
  FilterEditorStore;

const initialState = (): FilterStoreState => ({
  filterDialogOpenViewId: null,
  anchor: null,
  hasApplied: false,
  builder: null,
  structure: null,
  hoveredGroupId: null,
  draggedConditionId: null,
  dropTarget: null,
  highlighted: new Set(),
  fading: new Set(),
  peek: null,
});

export const useFilterStore = create<FilterStore>((set, get) => {
  // Imperative only: a ref registering must not re-render the editor.
  const elements = new Map<string, HTMLElement>();
  const timers = new Map<string, number>();
  let peekClosing: number | undefined;

  /** Every builder mutation: some return the next builder, some mutate in place. */
  const edit = (
    what: string,
    step: (builder: FilterGroupBuilder) => FilterGroupBuilder | void,
  ) => {
    const { builder } = get();
    if (!builder) return;
    try {
      const next = step(builder) ?? builder;
      set({ builder: next, structure: next.get_structure() });
    } catch (err) {
      logger.error(`Failed to ${what}`, err);
    }
  };

  /** Scrolls an item into view once it has drawn. */
  const reveal = (id: string) =>
    window.setTimeout(
      () =>
        elements
          .get(id)
          ?.scrollIntoView({ behavior: "smooth", block: "nearest" }),
      100,
    );

  /** Puts an id in one of the timed sets for `HIGHLIGHT_MS`. */
  const light = (which: "highlighted" | "fading", id: string) => {
    const change = (step: (ids: Set<string>) => void) =>
      set((s) => {
        const ids = new Set(s[which]);
        step(ids);
        return { [which]: ids };
      });
    change((ids) => ids.add(id));
    const key = `${which}:${id}`;
    window.clearTimeout(timers.get(key));
    timers.set(
      key,
      window.setTimeout(() => {
        change((ids) => ids.delete(id));
        timers.delete(key);
      }, HIGHLIGHT_MS),
    );
  };

  const highlight = (id: string) => {
    light("highlighted", id);
    reveal(id);
  };
  /** Adds under `groupId` (the root when null) and lights what was added. */
  const addItem = (
    what: string,
    groupId: string | null,
    add: (builder: FilterGroupBuilder) => FilterGroupBuilder,
  ): string | null => {
    let id: string | undefined;
    edit(what, (builder) => {
      const next = add(builder);
      id = next.get_newly_created_item_id(groupId ?? next.get_structure().id);
      return next;
    });
    if (id) highlight(id);
    return id ?? null;
  };

  return {
    ...initialState(),

    // Seeded with the view's filter, or one blank condition.
    openFilterDialog: (viewId, filter, anchor = null) => {
      set({ filterDialogOpenViewId: viewId, anchor, hasApplied: !!filter });
      try {
        const builder = filter
          ? FilterGroupBuilder.new_with_filter(filter)
          : FilterGroupBuilder.newjs(LogicalOperator.And);
        set({ builder, structure: builder.get_structure() });
      } catch (err) {
        logger.error("Failed to initialize builder", err);
        set({ builder: null, structure: null });
      }
    },

    openFilterFor: (viewId, filter, column, { anchor, cellValue } = {}) => {
      get().openFilterDialog(viewId, filter, anchor);
      const structure = get().structure;
      if (!structure) return null;
      // What the read view lit eases out; of a cell's value, only the parts no
      // condition names yet are added.
      const already = conditionsAbout(structure, column.id, cellValue);
      already.forEach((id) => light("fading", id));
      const missing =
        cellValue === undefined
          ? []
          : unnamedParts(structure, column.id, column.dataType, cellValue);
      if (already.length && !missing.length) {
        reveal(already[0]);
        return already[0];
      }
      // The blank condition a new filter starts on is filled before one is added.
      const id =
        structure.items
          .map((item) => item.as_condition())
          .find((c) => c && !c.field)?.id ?? get().addCondition(null);
      if (!id) return null;
      get().updateConditionField(id, column.id, column.name, column.dataType);
      const operator =
        cellValue === undefined
          ? null
          : cellOperator(column.dataType, cellValue);
      if (operator !== null) {
        // Several categories or files: a Contains each. A list is its whole
        // value, one Equal.
        const [first, ...rest] = missing.filter((part) => part !== null);
        get().updateConditionOperator(id, operator);
        if (first) get().updateConditionValue(id, first, column.dataType);
        for (const category of rest) {
          const added = get().addCondition(null);
          if (!added) continue;
          get().updateConditionField(
            added,
            column.id,
            column.name,
            column.dataType,
          );
          get().updateConditionOperator(added, operator);
          get().updateConditionValue(added, category, column.dataType);
        }
      }
      return id;
    },

    // The read view it may have opened from goes with it: the pointer held
    // that, so nothing else would release it.
    closeDialogAndReset: () => {
      window.clearTimeout(peekClosing);
      timers.forEach((timer) => window.clearTimeout(timer));
      timers.clear();
      elements.clear();
      set(initialState());
    },

    cancelEdit: () => {
      const {
        filterDialogOpenViewId: viewId,
        anchor,
        peek,
        hasApplied,
      } = get();
      get().closeDialogAndReset();
      if (viewId && anchor && hasApplied)
        set({
          peek:
            peek?.viewId === viewId && peek.anchor === anchor
              ? peek
              : { viewId, anchor },
        });
    },
    setPeek: (peek) => {
      window.clearTimeout(peekClosing);
      set({ peek });
    },
    releasePeek: () => {
      window.clearTimeout(peekClosing);
      peekClosing = window.setTimeout(
        () => set((s) => (s.peek?.pinned ? s : { peek: null })),
        PEEK_GRACE_MS,
      );
    },
    holdPeek: () => window.clearTimeout(peekClosing),

    addCondition: (groupId) =>
      addItem("add condition", groupId, (b) =>
        b.add_empty_conditionjs(groupId),
      ),
    addGroup: (groupId) =>
      addItem("add group", groupId, (b) => b.add_empty_nested_group(groupId)),
    removeCondition: (id) =>
      edit("remove condition", (b) => b.remove_conditionjs(id)),
    removeGroup: (id) => edit("remove group", (b) => b.remove_groupjs(id)),
    setGroupOperator: (id, operator) =>
      edit("set group operator", (b) => b.set_group_operator(id, operator)),
    moveCondition: (id, groupId, index) => {
      edit("move condition", (b) => b.move_condition(id, groupId, index));
      highlight(id);
    },

    updateConditionField: (id, fieldId, columnName, dataType) =>
      edit("update condition field", (b) =>
        b.updateConditionFieldWithName(
          id,
          fieldId,
          columnName ?? null,
          dataType ?? null,
        ),
      ),
    updateConditionOperator: (id, operator: FilterOperator) =>
      edit("update condition operator", (b) =>
        b.update_condition_operator(id, operator),
      ),
    updateConditionValue: (id, value, type) =>
      edit("update condition value", (b) =>
        b.update_condition_value_raw(id, type ?? undefined, value || undefined),
      ),
    updateConditionCaseSensitive: (id, caseSensitive) =>
      edit("update case sensitivity", (b) =>
        b.update_condition_case_sensitive(id, caseSensitive),
      ),
    addArrayValue: (id, value) => {
      const trimmed = value.trim();
      if (!trimmed) return;
      edit("add value", (b) => {
        const values = b.get_condition(id)?.string_array ?? [];
        if (values.includes(trimmed)) return;
        b.update_condition_value_raw(
          id,
          undefined,
          JSON.stringify([...values, trimmed]),
        );
      });
    },
    removeArrayItem: (id, index) =>
      edit("remove value", (b) =>
        b.remove_item_from_string_array_by_index(id, index),
      ),

    setHoveredGroupId: (hoveredGroupId) => set({ hoveredGroupId }),
    setDraggedConditionId: (draggedConditionId) =>
      set(
        draggedConditionId
          ? { draggedConditionId }
          : { draggedConditionId: null, dropTarget: null },
      ),
    setDropTarget: (dropTarget) => set({ dropTarget }),
    registerElement: (id, element) => {
      if (element) elements.set(id, element);
      else elements.delete(id);
    },
  };
});
